import { REVIEW_TEST_IDS } from '@/components/admin/sessions/contracts';
import { stampLine } from '@/lib/review-labels';
import type { ReviewStamp as Stamp } from '@matter_extractor/shared';
import type { ReactNode } from 'react';

/**
 * Who set an element and when (relecture.md §3.3, D2), and for the assistant
 * « via Claude · d'après … » (D20, D21): the line under every extract, note,
 * validation, annotation and noyau.
 *
 * « via Claude » is a pill in the bleu ciel of an information, never a warning:
 * the element is as valid as any other - the signature is the key-holder's, and
 * the « d'après » says whose judgement it carries.
 */

export interface ReviewStampProps {
  readonly stamp: Stamp;
  /** A gesture that belongs on the same line, after the stamp: « noter », « modifier ». */
  readonly children?: ReactNode;
}

export function ReviewStamp({ stamp, children }: ReviewStampProps) {
  const line = stampLine(stamp);
  return (
    <span
      data-testid={REVIEW_TEST_IDS.stamp}
      data-by={stamp.by}
      className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-gray-600 text-xs"
    >
      <span>
        {line.who} · {line.when}
      </span>
      {line.viaClaude ? (
        <span
          data-testid={REVIEW_TEST_IDS.via}
          className="inline-flex items-center rounded-sm bg-bleu-ciel-tint px-1.5 font-semibold text-bleu-ciel-deep text-micro"
        >
          {line.dapres === null ? 'via Claude' : `via Claude · d’après ${line.dapres}`}
        </span>
      ) : null}
      {children}
    </span>
  );
}
